import { useEffect, useState } from 'react';

// Minimal typed surface for the slice of the Google Maps JS API we use (avoids pulling in
// @types/google.maps). Mirrors the script-loader pattern in public/Turnstile.tsx.

export interface GLatLngLiteral {
  lat: number;
  lng: number;
}
export interface GLatLng {
  lat(): number;
  lng(): number;
}
export interface GMarker {
  setMap(map: GMap | null): void;
  getPosition(): GLatLng | null;
  addListener(event: string, handler: () => void): void;
}
export interface GInfoWindow {
  open(opts: { anchor?: GMarker; map?: GMap }): void;
  setContent(content: string | Node): void;
  close(): void;
}
export interface GBounds {
  extend(point: GLatLngLiteral): void;
  isEmpty(): boolean;
}
export interface GMap {
  fitBounds(bounds: GBounds, padding?: number): void;
  setCenter(point: GLatLngLiteral): void;
  setZoom(zoom: number): void;
  panTo(point: GLatLngLiteral): void;
  getZoom(): number | undefined;
  addListener(event: string, handler: () => void): void;
}
export interface GGeocoderResult {
  formatted_address: string;
  geometry: { location: GLatLng };
}
export interface GGeocoderRequest {
  address: string;
  componentRestrictions?: { country: string };
}
export interface GGeocoder {
  geocode(request: GGeocoderRequest): Promise<{ results: GGeocoderResult[] }>;
}

export interface GoogleMapsApi {
  maps: {
    Map: new (el: HTMLElement, opts: Record<string, unknown>) => GMap;
    Marker: new (opts: Record<string, unknown>) => GMarker;
    InfoWindow: new (opts?: Record<string, unknown>) => GInfoWindow;
    LatLngBounds: new () => GBounds;
    Geocoder: new () => GGeocoder;
    event: { clearInstanceListeners: (instance: object) => void };
  };
}

declare global {
  interface Window {
    google?: GoogleMapsApi;
    __maxsoldGmapsReady?: () => void;
    /** Google calls this when the API key fails to authorize (bad key / referrer / billing / API). */
    gm_authFailure?: () => void;
  }
}

// Auth failures surface asynchronously (after the script loads, on first API use), so we track them
// module-side and notify subscribed hooks.
let authFailed = false;
const authListeners = new Set<() => void>();
function markAuthFailed() {
  authFailed = true;
  authListeners.forEach((l) => l());
}

let loadPromise: Promise<GoogleMapsApi> | null = null;

/** Inject the Google Maps JS API exactly once (shared across the page + map component). */
function loadGoogleMaps(apiKey: string): Promise<GoogleMapsApi> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.maps) return Promise.resolve(window.google);
  if (loadPromise) return loadPromise;

  window.gm_authFailure = markAuthFailed;
  loadPromise = new Promise<GoogleMapsApi>((resolve, reject) => {
    window.__maxsoldGmapsReady = () => {
      if (window.google?.maps) resolve(window.google);
      else reject(new Error('Google Maps loaded without the maps namespace'));
    };
    const s = document.createElement('script');
    // No extra libraries: the map uses core classes and location search uses Geocoder (core).
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&callback=__maxsoldGmapsReady`;
    s.async = true;
    s.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Google Maps'));
    };
    document.head.appendChild(s);
  });
  return loadPromise;
}

const AUTH_FAILURE_MESSAGE =
  'Google rejected the Maps API key. In the Cloud console check that the key allows this site (HTTP referrers), billing is enabled, and the Maps JavaScript API + Geocoding API are enabled.';

export interface UseGoogleMaps {
  google: GoogleMapsApi | null;
  loaded: boolean;
  error: string | null;
}

/** Load the Google Maps JS API for a given key. No-op (never loaded) when `apiKey` is null. */
export function useGoogleMaps(apiKey: string | null): UseGoogleMaps {
  const [state, setState] = useState<UseGoogleMaps>({ google: null, loaded: false, error: null });

  useEffect(() => {
    if (!apiKey) {
      setState({ google: null, loaded: false, error: null });
      return;
    }
    let cancelled = false;
    const onAuthFail = () => {
      if (!cancelled) setState((s) => ({ ...s, error: AUTH_FAILURE_MESSAGE }));
    };
    authListeners.add(onAuthFail);
    if (authFailed) onAuthFail();

    loadGoogleMaps(apiKey)
      .then((google) => {
        if (!cancelled) setState((s) => ({ google, loaded: true, error: s.error }));
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setState({ google: null, loaded: false, error: e instanceof Error ? e.message : 'Map failed to load' });
      });

    return () => {
      cancelled = true;
      authListeners.delete(onAuthFail);
    };
  }, [apiKey]);

  return state;
}
